import { BookOpen, Mail, Car, MailX, HeartHandshake, Shield, Vote } from "lucide-react";

import cancelPassportImg from "@/shared/assets/illustrations/estate_essentials/cancel-passport.svg";
import voteImg from "@/shared/assets/illustrations/estate_essentials/vote.svg";
import doNotMailImg from "@/shared/assets/illustrations/estate_essentials/do-not-mail.svg";
import mailForwardingImg from "@/shared/assets/illustrations/estate_essentials/mail-forwarding.svg";
import socialSecurityImg from "@/shared/assets/illustrations/estate_essentials/social-security.svg";
import transferVehicleImg from "@/shared/assets/illustrations/estate_essentials/transfer-vehicle.svg";
import veteransBenefitsImg from "@/shared/assets/illustrations/estate_essentials/veterans-benefits.svg";
import documentSearchImg from "@/shared/assets/illustrations/document-search.svg";
import { US_STATES } from "@/shared/constants/constants";
import { RELATIONSHIP_OPTIONS } from "@/shared/constants/relationshipOptions";
import { WHY_WE_ASK, WHY_WE_ASK_TITLE } from "@/shared/constants/whyWeAsk";
import type {
  EssentialCategory,
  EssentialModule,
  EssentialModuleId,
  FormField,
  FormSectionConfig,
} from "@/shared/types/estateEssentials";

const stateOptions = US_STATES.map((s) => ({ value: s.value, label: s.label }));

export const MODULE_IMAGES: Record<EssentialModuleId, string> = {
  "cancel-passport": cancelPassportImg,
  "notify-credit-bureaus": documentSearchImg,
  "mail-forwarding": mailForwardingImg,
  "do-not-mail": doNotMailImg,
  "vehicle-boat-titles": transferVehicleImg,
  "voter-registration": voteImg,
  "social-security-benefits": socialSecurityImg,
  "veterans-survivor-benefits": veteransBenefitsImg,
};

export const CATEGORY_BG: Record<EssentialCategory, string> = {
  "probate-readiness": "bg-green-50",
  identity: "bg-blue-50",
  transfers: "bg-purple-50",
  benefits: "bg-orange-50",
};

const relationshipOptions = RELATIONSHIP_OPTIONS.map((r) => ({
  value: r.value,
  label: r.label,
}));

const MAIL_FORWARDING_FORM_SECTIONS: FormSectionConfig[] = [
  { key: "review_info", label: "Review information", mode: "review" },
  { key: "forward_address", label: "Where should {{ firstName }}'s mail go?", mode: "edit" },
];

const MAIL_FORWARDING_FIELDS: FormField[] = [
  {
    key: "deceased_full_name",
    label: "Full legal name",
    type: "text",
    required: true,
    autoPopulateKey: "deceased_full_name",
    section: "review",
    sectionGroup: "review_info",
  },
  {
    key: "deceased_address",
    label: "Street address",
    type: "text",
    required: true,
    autoPopulateKey: "deceased_address",
    section: "review",
    sectionGroup: "review_info",
    reviewCombine: "deceased_mailing_address",
    reviewLabel: "mailing_address",
  },
  {
    key: "deceased_city",
    label: "City",
    type: "text",
    required: true,
    autoPopulateKey: "deceased_city",
    section: "review",
    sectionGroup: "review_info",
    reviewCombine: "deceased_mailing_address",
  },
  {
    key: "deceased_state",
    label: "State",
    type: "select",
    required: true,
    options: stateOptions,
    autoPopulateKey: "deceased_state",
    section: "review",
    sectionGroup: "review_info",
    reviewCombine: "deceased_mailing_address",
  },
  {
    key: "deceased_zip",
    label: "ZIP code",
    type: "text",
    required: true,
    autoPopulateKey: "deceased_zip",
    section: "review",
    sectionGroup: "review_info",
    reviewCombine: "deceased_mailing_address",
  },
  {
    key: "user_full_name",
    label: "Your full name",
    type: "text",
    required: true,
    autoPopulateKey: "user_full_name",
    section: "review",
    sectionGroup: "review_info",
  },
  {
    key: "relationship_to_deceased",
    label: "Your legal relationship",
    type: "select",
    required: true,
    options: relationshipOptions,
    autoPopulateKey: "user_relationship",
    section: "review",
    sectionGroup: "review_info",
  },
  {
    key: "forward_address",
    label: "Street Address",
    type: "address",
    required: true,
    autoPopulateKey: "user_address",
    section: "editable",
    sectionGroup: "forward_address",
  },
  {
    key: "forward_apartment",
    label: "Apartment, building, suite",
    type: "text",
    required: false,
    placeholder: "Optional",
    autoPopulateKey: "user_apartment",
    section: "editable",
    sectionGroup: "forward_address",
  },
  {
    key: "forward_city",
    label: "City",
    type: "text",
    required: true,
    autoPopulateKey: "user_city",
    section: "editable",
    sectionGroup: "forward_address",
  },
  {
    key: "forward_state",
    label: "State",
    type: "select",
    required: true,
    options: stateOptions,
    autoPopulateKey: "user_state",
    section: "editable",
    sectionGroup: "forward_address",
  },
  {
    key: "forward_zip",
    label: "Zip Code",
    type: "text",
    required: true,
    autoPopulateKey: "user_zip",
    section: "editable",
    sectionGroup: "forward_address",
  },
];

const CANCEL_PASSPORT_FIELDS: FormField[] = [
  {
    key: "deceased_passport_number",
    label: "Passport number",
    type: "text",
    required: true,
    placeholder: "e.g. A12345678",
    autoPopulateKey: "deceased_passport_number",
    section: "review",
    tooltip: {
      title: WHY_WE_ASK_TITLE,
      content:
        "The State Department cancels a passport by its number, so it goes on the letter you'll mail. Cancelling it keeps anyone else from using it.",
    },
  },
  {
    key: "date_of_birth",
    label: "Date of birth",
    type: "date",
    required: true,
    autoPopulateKey: "date_of_birth",
    section: "review",
    row: "dates",
  },
  {
    key: "date_of_death",
    label: "Date of passing",
    type: "date",
    required: true,
    autoPopulateKey: "date_of_death",
    section: "review",
    row: "dates",
  },
  {
    key: "deceased_address",
    label: "Street Address",
    type: "address",
    required: true,
    autoPopulateKey: "deceased_address",
    section: "review",
    row: "dec_addr",
    reviewCombine: "deceased_full_address",
    reviewLabel: "address",
  },
  {
    key: "deceased_apartment",
    label: "Apartment, building, suite",
    type: "text",
    required: false,
    placeholder: "Optional",
    autoPopulateKey: "deceased_apartment",
    section: "review",
    row: "dec_addr",
    reviewCombine: "deceased_full_address",
  },
  {
    key: "deceased_city",
    label: "City",
    type: "text",
    required: true,
    autoPopulateKey: "deceased_city",
    section: "review",
    row: "dec_csz",
    reviewCombine: "deceased_full_address",
  },
  {
    key: "deceased_state",
    label: "State",
    type: "select",
    required: true,
    options: stateOptions,
    autoPopulateKey: "deceased_state",
    section: "review",
    row: "dec_csz",
    reviewCombine: "deceased_full_address",
  },
  {
    key: "deceased_zip",
    label: "Zip Code",
    type: "text",
    required: true,
    autoPopulateKey: "deceased_zip",
    section: "review",
    row: "dec_csz",
    reviewCombine: "deceased_full_address",
  },
  {
    key: "user_first_name",
    label: "First name",
    type: "text",
    required: true,
    autoPopulateKey: "user_first_name",
    section: "editable",
    row: "user_name",
    reviewCombine: "user_full_name",
    reviewLabel: "Your name",
  },
  {
    key: "user_last_name",
    label: "Last name",
    type: "text",
    required: true,
    autoPopulateKey: "user_last_name",
    section: "editable",
    row: "user_name",
    reviewCombine: "user_full_name",
  },
  {
    key: "user_relationship",
    label: "I am their...",
    type: "select",
    required: true,
    options: [...RELATIONSHIP_OPTIONS],
    autoPopulateKey: "user_relationship",
    section: "editable",
  },
  {
    key: "user_phone",
    label: "Phone number",
    type: "text",
    required: true,
    autoPopulateKey: "user_phone",
    section: "editable",
    row: "user_contact",
  },
  {
    key: "user_address",
    label: "Street address",
    type: "address",
    required: true,
    autoPopulateKey: "user_address",
    section: "editable",
    reviewCombine: "user_full_address",
    reviewLabel: "Mailing address",
  },
  {
    key: "user_city",
    label: "City",
    type: "text",
    required: true,
    autoPopulateKey: "user_city",
    section: "editable",
    reviewCombine: "user_full_address",
  },
  {
    key: "user_state",
    label: "State",
    type: "select",
    required: true,
    options: stateOptions,
    autoPopulateKey: "user_state",
    section: "editable",
    reviewCombine: "user_full_address",
  },
  {
    key: "user_zip",
    label: "ZIP code",
    type: "text",
    required: true,
    autoPopulateKey: "user_zip",
    section: "editable",
    reviewCombine: "user_full_address",
  },
];

const SOCIAL_SECURITY_FIELDS: FormField[] = [
  {
    key: "deceased_full_name",
    label: "Full legal name",
    type: "text",
    required: true,
    autoPopulateKey: "deceased_full_name",
    section: "review",
    row: "name_ssn",
  },
  {
    key: "deceased_ss_number",
    label: "Social Security number",
    type: "ssn",
    required: true,
    autoPopulateKey: "deceased_ss_number",
    section: "review",
    row: "name_ssn",
  },
  {
    key: "date_of_birth",
    label: "Date of birth",
    type: "date",
    required: true,
    autoPopulateKey: "date_of_birth",
    section: "review",
    row: "dates",
  },
  {
    key: "date_of_death",
    label: "Date of passing",
    type: "date",
    required: true,
    autoPopulateKey: "date_of_death",
    section: "review",
    row: "dates",
  },
  {
    key: "place_of_death",
    label: "Place of passing",
    type: "select",
    required: false,
    options: stateOptions,
    autoPopulateKey: "place_of_death",
    section: "review",
  },
  {
    key: "user_ss_number",
    label: "Your Social Security number",
    type: "ssn",
    required: true,
    autoPopulateKey: "user_ss_number",
    section: "editable",
    row: "user_ssn_rel",
    tooltip: {
      title: WHY_WE_ASK_TITLE,
      content:
        "Social Security pays survivor benefits to you, not to the estate, so the application needs your own number. It's encrypted, and only you can see it.",
    },
  },
  {
    key: "relationship_to_deceased",
    label: "I am their...",
    type: "select",
    required: true,
    options: relationshipOptions,
    autoPopulateKey: "user_relationship",
    section: "editable",
    row: "user_ssn_rel",
  },
  {
    key: "marriage_date",
    label: "Marriage date",
    type: "date",
    required: false,
    autoPopulateKey: "marriage_date",
    section: "editable",
    row: "marriage",
  },
  {
    key: "marriage_place",
    label: "Marriage place",
    type: "select",
    required: false,
    options: stateOptions,
    autoPopulateKey: "marriage_place",
    section: "editable",
    row: "marriage",
  },
  {
    key: "user_dob",
    label: "Your date of birth",
    type: "date",
    required: true,
    autoPopulateKey: "user_dob",
    section: "editable",
  },
];

const VEHICLE_FORM_SECTIONS: FormSectionConfig[] = [
  { key: "background", label: "Background information", mode: "review" },
  { key: "vehicle_count", label: "How many vehicles would you like to transfer?", mode: "edit" },
  { key: "transfer_info", label: "Transfer information", mode: "edit" },
];

const VEHICLE_TITLE_FIELDS: FormField[] = [
  {
    key: "deceased_full_name",
    label: "{{ firstName }}'s full legal name",
    type: "text",
    required: true,
    autoPopulateKey: "deceased_full_name",
    section: "review",
    sectionGroup: "background",
  },
  {
    key: "user_full_name",
    label: "Your name",
    type: "text",
    required: true,
    autoPopulateKey: "user_full_name",
    section: "review",
    sectionGroup: "background",
  },
  {
    key: "vehicle_count",
    label: "",
    type: "stepper",
    required: true,
    section: "editable",
    sectionGroup: "vehicle_count",
  },
  {
    key: "vehicle_make_model",
    label: "Make and model",
    type: "text",
    required: true,
    placeholder: "e.g., Toyota Camry",
    section: "editable",
    sectionGroup: "transfer_info",
    row: "make_year",
  },
  {
    key: "vehicle_year",
    label: "Year",
    type: "numeric",
    required: true,
    placeholder: "e.g., 2018",
    section: "editable",
    sectionGroup: "transfer_info",
    row: "make_year",
  },
  {
    key: "vehicle_state",
    label: "Vehicle state",
    type: "select",
    required: true,
    options: stateOptions,
    placeholder: "e.g., California",
    section: "editable",
    sectionGroup: "transfer_info",
    row: "state_plate",
  },
  {
    key: "license_plate_number",
    label: "License plate number",
    type: "text",
    required: true,
    placeholder: "e.g., 7ABC123",
    section: "editable",
    sectionGroup: "transfer_info",
    row: "state_plate",
  },
  {
    key: "vehicle_VIN",
    label: "VIN",
    type: "text",
    required: true,
    placeholder: "e.g., 1HGBH41JXMN109186",
    section: "editable",
    sectionGroup: "transfer_info",
    row: "vin_body",
    tooltip: {
      title: WHY_WE_ASK_TITLE,
      content:
        "The DMV uses the VIN to find the exact vehicle when you transfer the title. You'll find it on the title, the registration, or the driver's side of the dashboard.",
    },
  },
  {
    key: "vehicle_body_type",
    label: "Body type",
    type: "text",
    required: true,
    placeholder: "e.g., Sedan, SUV, Truck",
    section: "editable",
    sectionGroup: "transfer_info",
    row: "vin_body",
  },
  {
    key: "transfer_recipient_name",
    label: "Transfer recipient's name",
    type: "text",
    required: true,
    placeholder: "Enter full legal name",
    section: "editable",
    sectionGroup: "transfer_info",
    row: "recipient_rel",
  },
  {
    key: "relationship_to_decedent",
    label: "I am {{ firstName }}'s...",
    type: "select",
    required: true,
    options: relationshipOptions,
    placeholder: "Select relationship",
    autoPopulateKey: "user_relationship",
    section: "editable",
    sectionGroup: "transfer_info",
    row: "recipient_rel",
  },
  {
    key: "other_heirs",
    label: "Other heir names (if applicable)",
    type: "text",
    required: false,
    placeholder: "Enter full legal names, separated by commas",
    section: "editable",
    sectionGroup: "transfer_info",
  },
];

const branchOptions = [
  { value: "Army", label: "Army" },
  { value: "Navy", label: "Navy" },
  { value: "Air Force", label: "Air Force" },
  { value: "Marines", label: "Marines" },
  { value: "Coast Guard", label: "Coast Guard" },
  { value: "Space Force", label: "Space Force" },
];

const dischargeOptions = [
  { value: "Honorable", label: "Honorable" },
  { value: "General Under Honorable Conditions", label: "General Under Honorable Conditions" },
  { value: "Other Than Honorable", label: "Other Than Honorable" },
  { value: "Dishonorable", label: "Dishonorable" },
  { value: "Unknown", label: "Unknown" },
];

const VETERANS_FORM_SECTIONS: FormSectionConfig[] = [
  { key: "review_info", label: "Review {{ firstName }}'s information", mode: "review" },
  { key: "military_info", label: "Military information", mode: "edit" },
];

const VETERANS_BENEFITS_FIELDS: FormField[] = [
  {
    key: "deceased_full_name",
    label: "Full legal name",
    type: "text",
    required: true,
    autoPopulateKey: "deceased_full_name",
    section: "review",
    sectionGroup: "review_info",
    row: "name_ssn",
  },
  {
    key: "deceased_ss_number",
    label: "Social Security number",
    type: "ssn",
    required: true,
    autoPopulateKey: "deceased_ss_number",
    section: "review",
    sectionGroup: "review_info",
    row: "name_ssn",
    tooltip: { title: WHY_WE_ASK_TITLE, content: WHY_WE_ASK.decedentSsn },
  },
  {
    key: "date_of_birth",
    label: "Date of birth",
    type: "date",
    required: true,
    autoPopulateKey: "date_of_birth",
    section: "review",
    sectionGroup: "review_info",
    row: "dates",
  },
  {
    key: "date_of_death",
    label: "Date of passing",
    type: "date",
    required: true,
    autoPopulateKey: "date_of_death",
    section: "review",
    sectionGroup: "review_info",
    row: "dates",
  },
  {
    key: "place_of_death",
    label: "Place of passing",
    type: "select",
    required: false,
    options: stateOptions,
    autoPopulateKey: "place_of_death",
    section: "review",
    sectionGroup: "review_info",
    row: "place_rel",
  },
  {
    key: "relationship_to_deceased",
    label: "I am {{ firstName }}'s...",
    type: "select",
    required: true,
    options: relationshipOptions,
    autoPopulateKey: "user_relationship",
    section: "review",
    sectionGroup: "review_info",
    row: "place_rel",
  },
  {
    key: "branch_of_service",
    label: "Branch of service",
    type: "select",
    required: true,
    options: branchOptions,
    autoPopulateKey: "branch_of_service",
    section: "editable",
    sectionGroup: "military_info",
    row: "branch_discharge",
  },
  {
    key: "discharge_status",
    label: "Discharge status",
    type: "select",
    required: false,
    options: dischargeOptions,
    autoPopulateKey: "discharge_status",
    section: "editable",
    sectionGroup: "military_info",
    row: "branch_discharge",
  },
  {
    key: "service_start_date",
    label: "Service start date",
    type: "date",
    required: false,
    autoPopulateKey: "service_start_date",
    section: "editable",
    sectionGroup: "military_info",
    row: "service_dates",
  },
  {
    key: "service_end_date",
    label: "Service end date",
    type: "date",
    required: false,
    autoPopulateKey: "service_end_date",
    section: "editable",
    sectionGroup: "military_info",
    row: "service_dates",
  },
];

const DO_NOT_MAIL_FIELDS: FormField[] = [
  {
    key: "deceased_address",
    label: "Street Address",
    type: "address",
    required: true,
    autoPopulateKey: "deceased_address",
    section: "review",
    reviewCombine: "deceased_mailing_address",
    reviewLabel: "mailing_address",
  },
  {
    key: "deceased_apartment",
    label: "Apartment, building, suite",
    type: "text",
    required: false,
    placeholder: "Optional",
    autoPopulateKey: "deceased_apartment",
    section: "review",
    reviewCombine: "deceased_mailing_address",
  },
  {
    key: "deceased_city",
    label: "City",
    type: "text",
    required: true,
    autoPopulateKey: "deceased_city",
    section: "review",
    reviewCombine: "deceased_mailing_address",
  },
  {
    key: "deceased_state",
    label: "State",
    type: "select",
    required: true,
    options: stateOptions,
    autoPopulateKey: "deceased_state",
    section: "review",
    reviewCombine: "deceased_mailing_address",
  },
  {
    key: "deceased_zip",
    label: "Zip Code",
    type: "text",
    required: true,
    autoPopulateKey: "deceased_zip",
    section: "review",
    reviewCombine: "deceased_mailing_address",
  },
  {
    key: "date_of_birth",
    label: "Date of birth",
    type: "date",
    required: true,
    autoPopulateKey: "date_of_birth",
    section: "review",
    row: "dates",
  },
  {
    key: "date_of_death",
    label: "Date of passing",
    type: "date",
    required: true,
    autoPopulateKey: "date_of_death",
    section: "review",
    row: "dates",
  },
  {
    key: "user_first_name",
    label: "First name",
    type: "text",
    required: true,
    autoPopulateKey: "user_first_name",
    section: "editable",
    row: "user_name",
    reviewCombine: "user_full_name",
    reviewLabel: "Your name",
  },
  {
    key: "user_last_name",
    label: "Last name",
    type: "text",
    required: true,
    autoPopulateKey: "user_last_name",
    section: "editable",
    row: "user_name",
    reviewCombine: "user_full_name",
  },
  {
    key: "relationship_to_deceased",
    label: "I am their...",
    type: "select",
    required: true,
    options: relationshipOptions,
    autoPopulateKey: "user_relationship",
    section: "editable",
  },
];

const CREDIT_BUREAU_FIELDS: FormField[] = [
  {
    key: "deceased_full_name",
    label: "Full legal name",
    type: "text",
    required: true,
    autoPopulateKey: "deceased_full_name",
    section: "review",
    row: "name_ssn",
  },
  {
    key: "deceased_ss_number",
    label: "Social Security Number",
    type: "ssn",
    required: true,
    autoPopulateKey: "deceased_ss_number",
    section: "review",
    row: "name_ssn",
    tooltip: { title: WHY_WE_ASK_TITLE, content: WHY_WE_ASK.decedentSsn },
  },
  {
    key: "date_of_birth",
    label: "Date of birth",
    type: "date",
    required: true,
    autoPopulateKey: "date_of_birth",
    section: "review",
    row: "dates",
  },
  {
    key: "date_of_death",
    label: "Date of passing",
    type: "date",
    required: true,
    autoPopulateKey: "date_of_death",
    section: "review",
    row: "dates",
  },
  {
    key: "deceased_address",
    label: "Street Address",
    type: "address",
    required: true,
    autoPopulateKey: "deceased_address",
    section: "review",
    row: "dec_addr",
    reviewCombine: "deceased_full_address",
    reviewLabel: "address",
  },
  {
    key: "deceased_apartment",
    label: "Apartment, building, suite",
    type: "text",
    required: false,
    placeholder: "Optional",
    autoPopulateKey: "deceased_apartment",
    section: "review",
    row: "dec_addr",
    reviewCombine: "deceased_full_address",
  },
  {
    key: "deceased_city",
    label: "City",
    type: "text",
    required: true,
    autoPopulateKey: "deceased_city",
    section: "review",
    row: "dec_csz",
    reviewCombine: "deceased_full_address",
  },
  {
    key: "deceased_state",
    label: "State",
    type: "select",
    required: true,
    options: stateOptions,
    autoPopulateKey: "deceased_state",
    section: "review",
    row: "dec_csz",
    reviewCombine: "deceased_full_address",
  },
  {
    key: "deceased_zip",
    label: "Zip Code",
    type: "text",
    required: true,
    autoPopulateKey: "deceased_zip",
    section: "review",
    row: "dec_csz",
    reviewCombine: "deceased_full_address",
  },
  {
    key: "user_first_name",
    label: "First name",
    type: "text",
    required: true,
    autoPopulateKey: "user_first_name",
    section: "editable",
    row: "user_name",
    reviewCombine: "user_full_name",
    reviewLabel: "Your name",
  },
  {
    key: "user_last_name",
    label: "Last name",
    type: "text",
    required: true,
    autoPopulateKey: "user_last_name",
    section: "editable",
    row: "user_name",
    reviewCombine: "user_full_name",
  },
  {
    key: "user_relationship",
    label: "I am their...",
    type: "select",
    required: true,
    options: [...RELATIONSHIP_OPTIONS],
    autoPopulateKey: "user_relationship",
    section: "editable",
  },
  {
    key: "user_phone",
    label: "Phone number",
    type: "text",
    required: true,
    autoPopulateKey: "user_phone",
    section: "editable",
    row: "user_contact",
  },
  {
    key: "user_address",
    label: "Street address",
    type: "address",
    required: true,
    autoPopulateKey: "user_address",
    section: "editable",
    reviewCombine: "user_full_address",
    reviewLabel: "Mailing address",
  },
  {
    key: "user_apartment",
    label: "Apartment, building, suite",
    type: "text",
    required: false,
    placeholder: "Optional",
    autoPopulateKey: "user_apartment",
    section: "editable",
    reviewCombine: "user_full_address",
  },
  {
    key: "user_city",
    label: "City",
    type: "text",
    required: true,
    autoPopulateKey: "user_city",
    section: "editable",
    reviewCombine: "user_full_address",
  },
  {
    key: "user_state",
    label: "State",
    type: "select",
    required: true,
    options: stateOptions,
    autoPopulateKey: "user_state",
    section: "editable",
    reviewCombine: "user_full_address",
  },
  {
    key: "user_zip",
    label: "ZIP code",
    type: "text",
    required: true,
    autoPopulateKey: "user_zip",
    section: "editable",
    reviewCombine: "user_full_address",
  },
];

export const ESSENTIAL_CATEGORIES: { id: EssentialCategory; label: string }[] = [
  { id: "probate-readiness", label: "Probate Readiness" },
  { id: "identity", label: "Identity" },
  { id: "transfers", label: "Transfers & Services" },
  { id: "benefits", label: "Benefits" },
];

export const ESSENTIAL_MODULES: EssentialModule[] = [
  {
    id: "notify-credit-bureaus",
    name: "Place a deceased alert to freeze {{ firstName }}'s credit",
    nameTemplate: "Place a deceased alert to freeze {{ firstName }}'s credit",
    description:
      "Stops [First name]'s information from appearing in background checks and consumer databases.",
    formTitle: "Notify {{ firstName }}'s credit bureaus",
    formDescription:
      "Elayne will notify the three major credit bureaus - Equifax, Experian, and TransUnion - so {{ deceased_first_name }}'s credit files are flagged and protected from misuse.",
    category: "identity",
    icon: Shield,
    canRequestFromElayne: false,
    renderMode: "form",
    formFields: CREDIT_BUREAU_FIELDS,
    tags: ["elayne-can-do"],
    taskId: "notify_credit_bureaus",
    sidebarPanels: [
      {
        title: "What happens next",
        content:
          "Once you submit, Elayne will notify Equifax, Experian, and TransUnion and request that {{ deceased_first_name }}'s credit file be flagged with a deceased indicator. This helps ensure no one can open new accounts or lines of credit in their name.",
      },
      {
        title: "Processing time",
        content:
          "Each bureau will review the request and place a deceased alert on {{ deceased_first_name }}'s credit file. This typically takes about 30 days. You don't need to follow up.",
      },
      {
        title: "Good to know",
        content:
          "Once the credit bureaus are notified, {{ deceased_first_name }}'s credit reports can no longer be used to open new accounts. If creditors run a credit check, they'll see the deceased flag and deny the application. This is one of the most important steps in protecting their identity.",
      },
    ],
    submittedSummary:
      "Elayne has notified credit bureaus on behalf of {{ deceased_first_name }}. Their credit files will be flagged so no one can misuse their information. This typically takes about 30 days. There's nothing else you need to do.",
    submittedNote: "One less thing to worry about.",
    submittedTimeline: [],
  },
  {
    id: "cancel-passport",
    name: "Cancel passport",
    nameTemplate: "Cancel {{ firstName }}'s passport",
    description: "Get a ready-to-mail cancellation letter for the State Department.",
    formTitle: "Passport cancellation guide",
    category: "identity",
    icon: BookOpen,
    canRequestFromElayne: false,
    formFields: CANCEL_PASSPORT_FIELDS,
    taskId: "cancel_passport",
    tags: ["save-time"],
  },
  {
    id: "voter-registration",
    name: "Close voter record",
    nameTemplate: "Close {{ firstName }}'s voter record",
    description: "Stops ballots and election mail from being sent in {{ firstName }}'s name.",
    category: "identity",
    icon: Vote,
    canRequestFromElayne: false,
    taskId: "voter_registration",
    formFields: [
      {
        key: "deceased_state",
        label: "State of registration",
        type: "select",
        required: true,
        options: stateOptions,
        autoPopulateKey: "deceased_state",
        section: "review",
      },
      {
        key: "date_of_birth",
        label: "Date of birth",
        type: "date",
        required: true,
        autoPopulateKey: "date_of_birth",
        section: "review",
        row: "dates",
      },
      {
        key: "date_of_death",
        label: "Date of passing",
        type: "date",
        required: true,
        autoPopulateKey: "date_of_death",
        section: "review",
        row: "dates",
      },
      {
        key: "user_first_name",
        label: "First name",
        type: "text",
        required: true,
        autoPopulateKey: "user_first_name",
        section: "editable",
        row: "user_name",
        reviewCombine: "user_full_name",
        reviewLabel: "Your name",
      },
      {
        key: "user_last_name",
        label: "Last name",
        type: "text",
        required: true,
        autoPopulateKey: "user_last_name",
        section: "editable",
        row: "user_name",
        reviewCombine: "user_full_name",
      },
      {
        key: "relationship_to_deceased",
        label: "I am their...",
        type: "select",
        required: true,
        options: relationshipOptions,
        autoPopulateKey: "user_relationship",
        section: "editable",
      },
    ],
    tags: ["save-time"],
  },
  {
    id: "mail-forwarding",
    name: "Set up mail forwarding",
    description: "Redirect mail so you don't miss tax forms or legal correspondence.",
    category: "transfers",
    icon: Mail,
    canRequestFromElayne: false,
    taskId: "mail_forwarding",
    formFields: MAIL_FORWARDING_FIELDS,
    formSections: MAIL_FORWARDING_FORM_SECTIONS,
    tags: ["time-sensitive", "save-time"],
  },
  {
    id: "do-not-mail",
    name: 'Place a "Do not mail" hold',
    description: "Stops junk mail and protects against fraud through opt-out services.",
    formDescription:
      "This stops advertisers from sending mail to {{ deceased_first_name }}. Once you submit, Elayne will file a request with the national Do Not Contact registry on your behalf.",
    category: "transfers",
    icon: MailX,
    canRequestFromElayne: false,
    tags: ["elayne-can-do", "save-time"],
    renderMode: "form",
    taskId: "do_not_mail",
    formFields: DO_NOT_MAIL_FIELDS,
    sidebarPanels: [
      {
        title: "What happens next",
        content:
          "Once you submit, Elayne will register {{ deceased_first_name }} with the ANA Deceased Do Not Contact List. This tells advertisers to remove {{ deceased_first_name }} from mailing lists. Most junk mail stops within 90 days.",
      },
      {
        title: "Processing time",
        content: "5 to 7 business days to submit. Up to 90 days for mail to fully stop.",
      },
      {
        title: "What this doesn't do",
        content:
          "This stops advertising mail only. Official mail, bills, and legal notices are not affected.",
      },
      {
        title: "$6 filing fee",
        content: "Covered by Elayne. No charge to you.",
      },
    ],
    submittedSummary:
      "Elayne is registering {{ deceased_first_name }} with the national Do Not Contact list.",
    submittedTimeline: [
      { title: "Submitted to Elayne", description: "" },
      {
        title: "Processing registration",
        description:
          "{{ deceased_first_name }} is now on the registry. Most advertisers will stop mailing within a few weeks.",
      },
      {
        title: "Mail slows down",
        description:
          "It can take up to 90 days for all senders to update their lists and for mail to fully stop.",
      },
    ],
  },
  {
    id: "vehicle-boat-titles",
    name: "Transfer vehicle and boat titles",
    description: "Guidance for transferring ownership or preparing vehicles for sale.",
    category: "transfers",
    icon: Car,
    canRequestFromElayne: false,
    formFields: VEHICLE_TITLE_FIELDS,
    formSections: VEHICLE_FORM_SECTIONS,
    tags: ["save-time"],
    taskId: "vehicle_boat_titles",
  },
  {
    id: "social-security-benefits",
    name: "Apply for Social Security survivor benefits",
    description: "Find out what you're eligible for and gather the right documents.",
    category: "benefits",
    icon: HeartHandshake,
    canRequestFromElayne: false,
    taskId: "social_security_benefits",
    formFields: SOCIAL_SECURITY_FIELDS,
    tags: ["save-time"],
  },
  {
    id: "veterans-survivor-benefits",
    name: "Apply for Veterans survivor benefits",
    description: "Determine what you qualify for and prepare your application.",
    category: "benefits",
    icon: Shield,
    canRequestFromElayne: false,
    taskId: "veterans_survivor_benefits",
    formFields: VETERANS_BENEFITS_FIELDS,
    formSections: VETERANS_FORM_SECTIONS,
    tags: ["save-time"],
  },
];

const moduleMap = new Map<EssentialModuleId, EssentialModule>(
  ESSENTIAL_MODULES.map((m) => [m.id, m]),
);

export function getModuleById(id: EssentialModuleId): EssentialModule | undefined {
  return moduleMap.get(id);
}
